import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, Users, Trash2, LogIn, LogOut, UserMinus, Crown,
  StickyNote, Image as ImageIcon, Code2, Send, LayoutList, MessageSquare, PenTool,
} from "lucide-react";
import api from "@/lib/api";
import { Avatar, timeAgo } from "@/components/common";
import { Markdown } from "@/components/Markdown";
import { MarkdownEditor } from "@/components/MarkdownEditor";
import { ImageUpload } from "@/components/ImageUpload";
import { CodeEditorField, LANGUAGES } from "@/components/CodeEditorField";
import { ProjectChat } from "@/components/build/ProjectChat";
import { ProjectWhiteboard } from "@/components/build/ProjectWhiteboard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Composer({ projectId, onPosted }) {
  const [tab, setTab] = useState("note");
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [code, setCode] = useState("");
  const [lang, setLang] = useState("javascript");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const payload = { content };
    if (tab === "sketch") payload.image_url = image;
    if (tab === "code") { payload.code = code; payload.code_language = lang; }
    if (!content.trim() && !payload.image_url && !payload.code) return toast.error("Add something first");
    setBusy(true);
    try {
      const res = await api.post(`/build/projects/${projectId}/updates`, payload);
      setContent(""); setImage(""); setCode("");
      onPosted(res.data);
      toast.success("Shared · +8 pts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post");
    } finally {
      setBusy(false);
    }
  };

  const tabs = [
    { id: "note", label: "Note", icon: StickyNote },
    { id: "sketch", label: "Sketch / Diagram", icon: ImageIcon },
    { id: "code", label: "Code", icon: Code2 },
  ];

  return (
    <div className="border border-border bg-card rounded-xl p-5" data-testid="build-composer">
      <div className="flex gap-1 border-b border-border mb-4">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`composer-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-cobalt text-cobalt" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "note" && (
        <div className="border border-border rounded-md p-3">
          <MarkdownEditor value={content} onChange={setContent} placeholder="Share an idea, decision, or a flow… (Markdown supported)" rows={3} testid="build-note-input" />
        </div>
      )}
      {tab === "sketch" && (
        <div className="space-y-3">
          <ImageUpload value={image} onChange={setImage} aspect="aspect-[16/9]" label="Upload a sketch, whiteboard photo or architecture diagram" testid="build-sketch-upload" />
          <input value={content} onChange={(e) => setContent(e.target.value)} placeholder="Add a caption (optional)"
            className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none" data-testid="build-sketch-caption" />
        </div>
      )}
      {tab === "code" && (
        <div className="space-y-3">
          <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="build-code-language"
            className="bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none">
            {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <CodeEditorField value={code} onChange={setCode} language={lang} />
          <input value={content} onChange={(e) => setContent(e.target.value)} placeholder="Explain the code (optional)"
            className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none" data-testid="build-code-caption" />
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button onClick={submit} disabled={busy} data-testid="build-post-button"
          className="inline-flex items-center gap-2 bg-cobalt text-white rounded-md px-5 py-2 text-sm font-semibold hover:bg-gold-hover transition-colors disabled:opacity-50">
          <Send size={14} /> Share
        </button>
      </div>
    </div>
  );
}

function UpdateCard({ u, canDelete, onDelete }) {
  return (
    <div className="border border-border bg-card rounded-xl p-5" data-testid={`update-${u.update_id}`}>
      <div className="flex items-center justify-between">
        <Link to={`/members/${u.author_id}`} className="flex items-center gap-2.5 group">
          <Avatar src={u.author.picture} name={u.author.name} size={34} />
          <div>
            <div className="text-sm font-semibold group-hover:text-cobalt transition-colors">{u.author.name}</div>
            <div className="text-xs text-muted-foreground">{timeAgo(u.created_at)}</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono uppercase tracking-wider bg-secondary text-cobalt rounded-full px-2 py-0.5">{u.kind}</span>
          {canDelete && (
            <button onClick={() => onDelete(u.update_id)} data-testid={`delete-update-${u.update_id}`}
              className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={14} /></button>
          )}
        </div>
      </div>
      {u.content && <div className="mt-3 text-sm"><Markdown>{u.content}</Markdown></div>}
      {u.image_url && <img src={u.image_url} alt="" className="mt-3 w-full rounded-lg border border-border" />}
      {u.code && (
        <div className="mt-3">
          <CodeEditorField value={u.code} language={u.code_language} readOnly minHeight={80} />
        </div>
      )}
    </div>
  );
}

export default function BuildProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("workspace");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/build/projects/${projectId}`);
      setP(res.data);
    } catch (e) {
      toast.error("Project not found");
      navigate("/build");
    }
  }, [projectId, navigate]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    try { const res = await fn(); setP(res.data); ok && toast.success(ok); }
    catch (e) {
      if (e.response?.status === 402) { toast.error("Ikigora Pro is required to collaborate"); navigate("/upgrade"); return; }
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const join = () => act(() => api.post(`/build/projects/${projectId}/join`), "Joined the project");
  const leave = () => act(() => api.post(`/build/projects/${projectId}/leave`), "Left the project");
  const removeMember = (id) => act(() => api.delete(`/build/projects/${projectId}/members/${id}`), "Member removed");
  const del = async () => {
    await api.delete(`/build/projects/${projectId}`);
    toast.success("Project deleted");
    navigate("/build");
  };
  const deleteUpdate = async (id) => {
    await api.delete(`/build/updates/${id}`);
    setP((prev) => ({ ...prev, updates: prev.updates.filter((x) => x.update_id !== id) }));
  };

  if (!p) return <div className="text-center py-16 text-muted-foreground eyebrow">Loading…</div>;

  const secondaryTabs = [
    { id: "workspace", label: "Workspace", icon: LayoutList },
    { id: "chat", label: "Discussion", icon: MessageSquare },
    { id: "whiteboard", label: "Whiteboard", icon: PenTool },
  ];

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/build")} data-testid="build-back-button"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ChevronLeft size={16} /> All projects
      </button>

      <div className="border border-border bg-card rounded-xl p-6 sm:p-8">
        <div className="flex flex-wrap gap-1.5 mb-3">
          {p.tags.map((t) => <span key={t} className="text-xs text-cobalt bg-secondary rounded-full px-2.5 py-0.5">{t}</span>)}
        </div>
        <div className="flex items-start justify-between gap-4">
          <h1 className="font-serif font-bold text-3xl sm:text-4xl tracking-tight">{p.title}</h1>
          <div className="flex items-center gap-2 shrink-0">
            {!p.is_member && !p.is_full && (
              <button onClick={join} data-testid="join-project-button"
                className="inline-flex items-center gap-1.5 bg-cobalt text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-gold-hover transition-colors">
                <LogIn size={15} /> Join
              </button>
            )}
            {!p.is_member && p.is_full && <span className="text-sm text-amber font-mono">Project full</span>}
            {p.is_member && !p.is_creator && (
              <button onClick={leave} data-testid="leave-project-button"
                className="inline-flex items-center gap-1.5 border border-border rounded-md px-4 py-2 text-sm hover:bg-secondary/60 transition-colors">
                <LogOut size={15} /> Leave
              </button>
            )}
            {p.is_creator && (
              <button onClick={del} data-testid="delete-project-button"
                className="inline-flex items-center gap-1.5 border border-destructive/40 text-destructive rounded-md px-4 py-2 text-sm hover:bg-destructive/10 transition-colors">
                <Trash2 size={15} /> Delete
              </button>
            )}
          </div>
        </div>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-2xl">{p.goal}</p>

        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="eyebrow text-muted-foreground">Collaborators</span>
            <span className={`inline-flex items-center gap-1.5 text-xs font-mono ${p.is_full ? "text-amber" : "text-emerald"}`}>
              <Users size={13} /> {p.member_count}/{p.seats} seats
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {p.members.map((m) => (
              <div key={m.user_id} className="flex items-center gap-2 border border-border rounded-full pl-1 pr-3 py-1" data-testid={`member-${m.user_id}`}>
                <Avatar src={m.picture} name={m.name} size={26} />
                <span className="text-xs">{m.name}</span>
                {m.user_id === p.creator_id && <Crown size={12} className="text-amber" title="Creator" />}
                {p.is_creator && m.user_id !== p.creator_id && (
                  <button onClick={() => removeMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}
                    className="text-muted-foreground hover:text-destructive ml-0.5"><UserMinus size={13} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {p.is_member ? (
        <>
          <div className="flex gap-1 border-b border-border">
            {secondaryTabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`project-tab-${t.id}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
                  tab === t.id ? "border-cobalt text-cobalt" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                <t.icon size={15} /> {t.label}
              </button>
            ))}
          </div>

          {tab === "workspace" && (
            <div className="space-y-4">
              <Composer projectId={projectId} onPosted={(u) => setP((prev) => ({ ...prev, updates: [...prev.updates, u] }))} />
              <div className="eyebrow text-cobalt">{p.updates.length} updates</div>
              {p.updates.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
                  Nothing here yet. Kick things off with the first note.
                </div>
              ) : (
                p.updates.map((u) => (
                  <UpdateCard key={u.update_id} u={u}
                    canDelete={user && (user.user_id === u.author_id || p.is_creator)}
                    onDelete={deleteUpdate} />
                ))
              )}
            </div>
          )}
          {tab === "chat" && <ProjectChat projectId={projectId} members={p.members} />}
          {tab === "whiteboard" && <ProjectWhiteboard projectId={projectId} />}
        </>
      ) : (
        <div className="border border-dashed border-border rounded-xl p-8 text-center text-sm text-muted-foreground">
          Join this project to open the workspace, live discussion and whiteboard.
        </div>
      )}
    </div>
  );
}
